import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { Mermaid } from "@/components/Mermaid";
import { IntroNote } from "@/components/IntroNote";
import { GLOSSARY } from "@/lib/glossary";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BookOpen, Compass, Search } from "lucide-react";

const tabCls =
  "rounded-sm px-5 py-2 text-sm font-medium text-ek-ink/65 data-[state=active]:bg-ek-purple data-[state=active]:text-white data-[state=active]:shadow-sm transition-colors";

const LIFECYCLE = `sequenceDiagram
  autonumber
  participant SRC as Signal source
  participant SYS as ERIO system
  participant AN as Analyst
  participant OWN as Risk owner
  participant EXEC as CRO / Exec
  SRC->>SYS: Inbound signal (connector)
  SYS->>AN: Normalised and correlated to risks
  AN->>OWN: Triage and link to a risk
  SYS->>OWN: Pre-mortem alert (projected score)
  OWN->>EXEC: Escalate or recommend playbook
  EXEC->>SYS: Approve playbook and remediation
  SYS->>SYS: Execute and record immutable audit`;

const ROLE_GUIDES = [
  {
    role: "Everyone — getting oriented",
    steps: [
      "Start on the Dashboard. The Overview tab shows your live Risk Posture index, KPIs and any appetite breaches.",
      "Use the top navigation to reach Risks, Suppliers and Signals; grouped menus hold Intelligence, Governance, Playbooks and Platform tools.",
      "Click the Ask ERIO button (bottom-right) to ask questions in plain language, e.g. ‘what are our top three risks?’.",
      "Switch language any time with the language selector in the header.",
    ],
  },
  {
    role: "Risk owner / Analyst",
    steps: [
      "Open Risks to see the enterprise risk graph; each risk shows inherent and residual scores (1–25) and linked signals.",
      "Review Signals and link relevant ones to the risks they affect — this feeds the pre-mortem projection.",
      "Watch the Pre-mortem alerts on the dashboard for risks trending upward, and act before they escalate.",
      "When a threshold is crossed, assign the recommended Playbook and track its run to completion.",
    ],
  },
  {
    role: "CRO / Executive",
    steps: [
      "Use the Dashboard Financial tab for quantified £ exposure (expected annual loss, loss-exceedance curve, top contributors).",
      "Use the Intelligence tab for live geopolitical/threat intelligence and the AI executive briefing.",
      "Set Risk appetite per theme; the dashboard flags any theme over tolerance automatically.",
      "Generate a board pack (PDF) or email it directly from the dashboard header.",
    ],
  },
  {
    role: "Administrator",
    steps: [
      "Manage people in Users & access; connect enterprise tools in Platform → Integrations (Jira, ServiceNow, SAP Ariba).",
      "Review the Admin audit log — every state-changing action is recorded.",
      "Use the Trust Centre (Governance) for attestations, evidence and reproducibility manifests.",
      "The proprietary Methodology reference is available to administrators under Platform → Methodology.",
    ],
  },
];


export default function Manual() {
  const [params] = useSearchParams();
  const termParam = params.get("term") || "";
  const [tab, setTab] = useState(termParam ? "glossary" : "howto");
  const [q, setQ] = useState(termParam);

  useEffect(() => {
    if (termParam) {
      setTab("glossary");
      setQ(termParam);
    }
  }, [termParam]);

  const filtered = GLOSSARY.filter(
    ([term, def]) => !q || (term + def).toLowerCase().includes(q.toLowerCase())
  );

  // First row id per starting letter, for the A–Z jump index.
  const letterAnchors = {};
  filtered.forEach(([term]) => {
    const L = term[0].toUpperCase();
    if (!letterAnchors[L]) letterAnchors[L] = term;
  });
  const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const jumpTo = (L) => document.getElementById(`gl-${L}`)?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="User guide" description="How to use ERIO and a glossary of every key term." path="/manual" />
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-10" data-testid="manual-page">
        <p className="ek-eyebrow">Help</p>
        <h1 className="mt-1 font-display text-3xl font-bold text-ek-ink">User guide & glossary</h1>
        <p className="mt-2 max-w-2xl text-sm text-ek-ink/65">
          Everything you need to find your way around ERIO — how the workflow fits together, role-by-role
          how-tos, and plain-English definitions of every term you’ll see.
        </p>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="h-auto flex-wrap justify-start gap-1 rounded-md border border-black/5 bg-white p-1" data-testid="manual-tabs">
            <TabsTrigger value="howto" className={tabCls} data-testid="manual-tab-howto"><Compass size={15} className="mr-1.5" /> How to use</TabsTrigger>
            <TabsTrigger value="glossary" className={tabCls} data-testid="manual-tab-glossary"><BookOpen size={15} className="mr-1.5" /> Glossary</TabsTrigger>
          </TabsList>

          <TabsContent value="howto" className="mt-6 focus:outline-none" data-testid="manual-panel-howto">
            <IntroNote icon={Compass} title="How to use" testid="manual-howto-intro">
              See how a single signal becomes assured action — who does what, and in what order — then follow
              the role-by-role steps below to get productive in minutes.
            </IntroNote>
            <h2 className="ek-eyebrow">The ERIO workflow</h2>
            <p className="mt-1 text-sm text-ek-ink/65">How a signal becomes assured action — and who does what.</p>
            <div className="mt-4">
              <Mermaid chart={LIFECYCLE} />
            </div>

            <div className="mt-10 grid gap-6 lg:grid-cols-2">
              {ROLE_GUIDES.map((g) => (
                <div key={g.role} data-testid={`role-guide-${g.role.split(" ")[0].toLowerCase()}`} className="rounded-md border border-black/5 bg-white p-6">
                  <h3 className="font-display text-lg font-semibold text-ek-ink">{g.role}</h3>
                  <ol className="mt-3 space-y-2.5">
                    {g.steps.map((s, i) => (
                      <li key={i} className="flex gap-3 text-sm text-ek-ink/65">
                        <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-ek-purple/10 text-xs font-bold text-ek-purple">{i + 1}</span>
                        <span className="leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="glossary" className="mt-6 focus:outline-none" data-testid="manual-panel-glossary">
            <IntroNote icon={BookOpen} title="Glossary" testid="manual-glossary-intro">
              Every key term in plain English, in alphabetical order, with where you'll encounter it. Use the
              search to jump straight to a term — handy when a score or label on screen isn't familiar.
            </IntroNote>
            <div className="relative max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ek-ink/30" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search terms…"
                data-testid="glossary-search"
                className="w-full rounded-md border border-black/10 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20"
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-1" data-testid="glossary-az-index">
              {ALPHABET.map((L) => {
                const active = !!letterAnchors[L];
                return (
                  <button
                    key={L}
                    onClick={() => active && jumpTo(L)}
                    disabled={!active}
                    data-testid={`glossary-jump-${L}`}
                    className={`h-7 w-7 rounded text-xs font-semibold transition-colors ${
                      active ? "text-ek-purple hover:bg-ek-purple hover:text-white" : "cursor-default text-ek-ink/20"
                    }`}
                  >
                    {L}
                  </button>
                );
              })}
            </div>
            <div className="mt-5 overflow-hidden rounded-md border border-black/5 bg-white" data-testid="glossary-table">
              <table className="w-full text-left text-sm">
                <thead className="bg-ek-canvas text-xs uppercase tracking-wide text-ek-ink/65">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Term</th>
                    <th className="px-5 py-3 font-semibold">What it means</th>
                    <th className="hidden px-5 py-3 font-semibold md:table-cell">Where you’ll see it</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(([term, def, where]) => {
                    const L = term[0].toUpperCase();
                    const isFirst = letterAnchors[L] === term;
                    return (
                      <tr key={term} id={isFirst ? `gl-${L}` : undefined} className="border-t border-black/5 align-top scroll-mt-24">
                        <td className="px-5 py-3 font-semibold text-ek-ink">{term}</td>
                        <td className="px-5 py-3 text-ek-ink/65">{def}</td>
                        <td className="hidden px-5 py-3 text-ek-ink/65 md:table-cell">{where}</td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={3} className="px-5 py-6 text-center text-ek-ink/65">No terms match “{q}”.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
